/**
 * Companion-audience adapter: the family's chat with their companion
 * (Maya by default, renamed per family through persona preferences).
 * Reached through `resolveChatConfig('companion')` (#1731, spec §4.1).
 */
import type { BrandChatConfig } from '@core/config/types';
import { useAuth } from '@/contexts/AuthContext';
import { COMPANION_NAME } from '@/lib/companion-config';
import { chatApi } from '@/services/chatApi';
import { voiceApi } from '@/services/voiceApi';
import { activeLanguage, companionCopy } from './copy/companion';
import { toCoreConversationSummary } from '@/features/chat-trf/adapter/conversations';
import {
  assertGrant,
  isStaleSessionConflict,
  getApiBase,
  getApiOrigin,
  getAuthHeaders,
  refreshAuthToken,
  toVoiceConversation,
  toVoiceMintError,
} from './endpoints';

export function companionChatConfig(
  agentName: string = COMPANION_NAME,
  language: string = activeLanguage(),
): BrandChatConfig {
  return {
    brand: 'companion',

    capabilities: {
      dictation: true,
      upload: true,
      // Phase 3 mounts the PiP; the flag is what makes the core render it.
      voicePip: true,
      // Composer typing into a live call. The family voice token always
      // carries the data grant (backend constants/voiceGrants.js, #3174); a
      // publish the SDK still refuses pauses the composer with
      // `copy.voice.duplexUnavailable`.
      duplex: true,
      actions: { copy: true, retry: true, feedback: false },
      historyDrawer: true,
      export: true,
      chips: true,
      // No brand-owned cards yet; unknown kinds render nothing.
      richCards: 'registered',
      // #1902: ON. Victor ruled the recall line should be visible
      // (2026-09-08), and it now has a producer: Clio emits `memory_recall`
      // from the SAME bundle that went into its prompt, gated on
      // `rendered_semantic()`, and omits the key entirely when nothing
      // material was recalled. So arrival of the frame IS the materiality
      // signal — there is no "recalled nothing" payload, and no line is drawn
      // for the absence of memory. Turning this back off is one word; what
      // must never happen is this being `true` with no producer, which is
      // what phase 7 found and switched off.
      recallLine: true,
    },

    // TRF has no document writer: the only PDF dependency in the backend
    // is `pdf-parse`, which READS PDFs. Until B-4 builds one, the modal
    // shows md, txt and print and claims nothing else.
    serverExportFormats: [],

    copy: companionCopy(agentName, undefined, language),

    endpoints: {
      getApiOrigin,
      getApiBase,
      getAuthHeaders,
      refreshAuthToken,
      getVoiceConversation: async (conversationId) =>
        toVoiceConversation(await chatApi.getHistory(conversationId)),
      // Mic first, mint second (spec §1.4): the widget asks for the mic
      // before it calls this.
      mintVoiceGrant: async (opts) => {
        // One record, two entries: a start from a thread reuses that thread;
        // a re-attach (`roomName` = the stored session's thread) reuses it too.
        //
        // #1731 phase 6: a re-attach also asks to REJOIN the LiveKit room it
        // dropped. Without that flag the route 409s, the fallback below ends the
        // live session and retries, and the retry mints a fresh room — which is
        // how one call became two Calliope sessions 73 seconds apart on
        // 2026-09-06 and doubled the last turn in the thread (#1805). A first
        // call never sets it, so the duplicate-session mutex is unchanged.
        const isReattach = Boolean(opts.roomName);
        const params = {
          conversationId: opts.roomName ?? opts.sourceConversationId ?? undefined,
          sourceConversationId: opts.sourceConversationId ?? undefined,
          rejoin: isReattach,
        };
        try {
          return assertGrant(await voiceApi.getToken(params));
        } catch (first) {
          if (!isStaleSessionConflict(first)) throw toVoiceMintError(first);
          await voiceApi.endSession().catch(() => {});
          try {
            return assertGrant(await voiceApi.getToken(params));
          } catch (second) {
            throw toVoiceMintError(second);
          }
        }
      },
      // The drawer's rows. Voice rows ride the same list (one record carries
      // both modalities), so the separate voice source is empty.
      conversations: {
        list: async (limit) => (await chatApi.getConversations({ limit })).map(toCoreConversationSummary),
        listVoice: async () => [],
        delete: async (conversationId) => {
          await chatApi.deleteConversation(conversationId);
          return true;
        },
        // #2404: PATCH /chat/conversations/:id/title (owner-checked, 1–100
        // chars). A failure rejects, and the drawer's dialog says so.
        rename: async (conversationId, title) => {
          await chatApi.renameConversation(conversationId, title);
          return true;
        },
      },
    },

    routes: {
      chat: '/chat',
      chatHistory: '/conversations',
    },

    hooks: {
      useCurrentUserEmail: () => useAuth().user?.email,
    },

    cards: {},

    agentDisplayName: () => agentName,
  };
}
